import React, {useCallback, useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Heading, Text} from '@albert-io/atomic';
import {callTargetedAction, setUpStore} from 'client/framework';

import FreeResponseStore from '../FreeResponseQuestion.store';
import freeResponseActions from '../FreeResponseQuestion.actions';
import {WSStore} from '../FreeResponseQuestion.types';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  answerInEnglishText,
  translatedInstructions
} from '../../shared/TranslationSection';
import useDraftGuess from '../../../../AssignmentPracticeView/DraftGuesses/useDraftGuess';

import WrittenSubmissionContext from './WrittenSubmission.context';
import WrittenSubmissionInput from './Editor/WrittenSubmissionInput.react';

interface Props {
  storeName: string;
  submitted?: boolean;
  question: any;
}

const WrittenStudentSubmission = ({storeName, submitted = false, question}: Props) => {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const {DraftGuessSaveStatus, startTimer} = useDraftGuess(storeName, submitted);

  const [store, setStore] = useState<null | WSStore>(null);
  const getWordCount = useCallback(() => {
    if (!store) return 0;
    return store.getWordCount();
  }, [store]);

  const onChange = useCallback(
    (data) => {
      if (!submitted) {
        callTargetedAction({
          name: freeResponseActions.UPDATE_RESPONSE,
          payload: data,
          targetStore: storeName
        });
      }
    },
    [storeName, submitted]
  );

  useEffect(() => {
    const newStore = setUpStore(FreeResponseStore, storeName, question.getId(), question);
    setStore(null);

    const timeout = setTimeout(() => {
      // hot swapping written submission input editor values causes a bug where if the previous cursor is out of bounds of the next
      // written submission, it causes an error. This only occurs when the next question is another written submission

      // FIX: Prevent unwanted scroll-to-textarea on initial page load.
      //
      // When navigating to an FRQ question from the subject guide, the page should
      // show the question prompt at the top. However, the Slate rich-text editor
      // renders a contenteditable div, and browsers natively auto-scroll overflow
      // containers to reveal newly-inserted contenteditable elements. This causes
      // the practice-view question area to jump down to the textarea on mount.
      //
      // We attach a scroll listener that synchronously resets scrollTop to 0,
      // preventing the browser's auto-scroll from ever being painted. The listener
      // is removed on the next frame once the contenteditable has stabilized.
      const container = document.querySelector('[data-testid="practice-view__question-area"]');
      const preventScroll = () => {
        if (container) container.scrollTop = 0;
      };
      container?.addEventListener('scroll', preventScroll);

      startTimer(); // start time_elapsed timer
      setStore(newStore);

      // Remove the scroll lock after the next frame so normal user scrolling
      // is not blocked. The browser's auto-scroll fires synchronously during
      // DOM insertion (same tick as setStore), so by the next animation frame
      // it has already been neutralized and the listener is no longer needed.
      requestAnimationFrame(() => {
        container?.removeEventListener('scroll', preventScroll);
      });
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [storeName, question, startTimer]);

  const editorState = store ? store.getValue() : null;
  const labelText = submitted ? 'Your Response' : 'Provide Your Response Below';

  const Wrapper = showTranslations ? NoTranslationWarningHeader : React.Fragment;

  const wordCountText = showTranslations ? 'Conteo de palabras' : 'Word Count';

  return (
    <div className='mb-x4 w-full'>
      <Heading as='h2' size='s' id='wss-text-input'>
        {showTranslations ? translatedInstructions.freeResponse : labelText}
      </Heading>
      <Wrapper className='mb-x2' text={answerInEnglishText}>
        {store && editorState && (
          <WrittenSubmissionContext.Provider value={{store, storeName, submitted}}>
            <WrittenSubmissionInput
              onChange={onChange}
              value={editorState}
              readOnly={submitted}
              aria-labelledby='wss-text-input'
            />
            {!submitted ? (
              <span className='mt-x1 flex items-center'>
                <DraftGuessSaveStatus store={store} />
                <Text size='s' className='ml-auto content-tertiary'>
                  {wordCountText}: {getWordCount()}
                </Text>
              </span>
            ) : null}
          </WrittenSubmissionContext.Provider>
        )}
      </Wrapper>
    </div>
  );
};

export default WrittenStudentSubmission;
